// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";
import "../../../theme/SwiperOverrides.css";
import "./youtube-lite.override.css";

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as styles from "./IntroTripleCarousel.module.scss";

import type { SwiperClass } from "swiper/react";
import type { HeroImageProps } from "../../../components/HeroImage/HeroImage";
import ImageCarouselModal from "../../../components/ImageCarouselModal/ImageCarouselModal";
import { Pagination } from "../../../components/Pagination/Pagination";
import YouTubeLite from "../../../components/YouTubeLite/YouTubeLite";
import { Expand } from "../../../components/common/Icons";
import MediaCarousel, {
	type MediaImageProps,
	type MediaVideoProps,
} from "../../../components/common/MediaCarousel/MediaCarousel";
import ProcessedImage from "../../../components/common/ProcessedImage/ProcessedImage";
import defaultDogImage from "../../../static/img/dogComingSoon.svg";

const rotateArray = (
	arr: (MediaImageProps | MediaVideoProps)[],
	positions: number,
) => {
	const newArray = [...arr];
	const removedElements = newArray.splice(0, positions);
	return newArray.concat(removedElements);
};

export interface IntroTripleCarouselProps {
	media: (MediaImageProps | MediaVideoProps)[];
	isDog?: boolean;
}
// TODO - Make sure images are kept to 4:3 ratio on mobile.

const DesktopImage: React.FC<HeroImageProps & { onOpenModal: () => void }> = ({
	src,
	alt,
	onOpenModal,
	isDog = true,
}) => {
	const [height, setHeight] = useState(0);
	const [width, setWidth] = useState(0);
	useEffect(() => {
		const resizeVideo = () => {
			const mainImageContainer = document.getElementById(
				"main-image-container",
			);
			setHeight(mainImageContainer.getBoundingClientRect().height);
			setWidth(mainImageContainer.getBoundingClientRect().width);
		};
		resizeVideo();

		window.addEventListener("resize", resizeVideo);
		return () => {
			window.removeEventListener("resize", resizeVideo);
		};
	}, []);
	return (
		<div
			className={styles.mediawrapper}
			style={{
				height,
				width,
			}}
		>
			<button
				type="button"
				className={styles.expand}
				onClick={onOpenModal}
				aria-label="Expand to full screen"
			>
				<Expand height={"24px"} width={"24px"} />
			</button>
			<ProcessedImage
				src={src}
				alt={alt}
				dimensions="800x600"
				width={800}
				height={600}
				isDogImage={isDog}
				preload
				loading="eager"
			/>
		</div>
	);
};

const SmallImage: React.FC<HeroImageProps> = ({ src, alt, isDog = true }) => (
	<div className={styles.mediawrapper}>
		{/* Doesn't make sense to use 400x300 when we would eventually load the
        800x600 anyway this means we're only loading one image size which is
        ultimately better for performance */}
		<ProcessedImage
			src={src}
			alt={alt}
			dimensions="400x300"
			width={400}
			height={300}
			preload
			isDogImage={isDog}
		/>
	</div>
);

const DogBioVideoPreview: React.FC<{ url?: string; youtubeId?: string }> = ({
	url,
	youtubeId,
}) => {
	let id = url || youtubeId || "";
	if (id?.includes("?v=")) {
		id = id.split("?v=")[1];
	}
	return (
		<div className={styles.mediawrapper}>
			<img
				src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`}
				width={400}
				height={300}
				alt={"A preview of the dog's video"}
			/>
		</div>
	);
};

export const IntroTripleCarousel: React.FC<IntroTripleCarouselProps> = ({
	media: mediaWithoutOnExpand,
	isDog = true,
}) => {
	const [topSwiper, setTopSwiper] = useState(null);
	const [bottomSwiper, setBottomSwiper] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [expanded, setExpanded] = useState(false);
	const swiperRef = useRef<{ swiper: SwiperClass }>(null);
	const media = mediaWithoutOnExpand.map((mediaItem) => ({
		...mediaItem,
		onOpenModal: () => setExpanded(true),
	}));

	const topCarouselMedia = useMemo(() => rotateArray(media, 1), [media]);
	const bottomCarouselMedia = useMemo(() => rotateArray(media, 2), [media]);

	return (
		<>
			<ImageCarouselModal
				expanded={expanded}
				isDog={isDog}
				images={media
					.filter((media) => media.type === "image")
					.map((fieldImage: MediaImageProps) => ({
						src: fieldImage.src,
						alt: fieldImage.alt,
					}))}
				onHide={() => setExpanded(false)}
				activeSlide={currentSlide}
			/>
			{media.length >= 1 ? (
				<div
					className={
						media.length < 3
							? styles.singleimagecontainer
							: styles.dogbiointrocontainer
					}
				>
					<div
						id="main-image-container"
						className={
							media.length >= 3
								? styles.carouselimagecontainer
								: styles.onlyimagecontainer
						}
					>
						<MediaCarousel
							ref={swiperRef}
							previewImageIndex={0}
							key={"main-carousel"}
							className={styles.swipermain}
							galleryItems={media}
							loop={false}
							CustomImageComponent={DesktopImage}
							CustomVideoComponent={YouTubeLite}
							onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
							pagination={false}
							allowTouchMove={false}
							grabCursor={false}
							longSwipes={false}
							resistance={false}
							shortSwipes={false}
							isDog={isDog}
						/>
					</div>
					{media.length >= 3 && (
						<>
							<div className={styles.previewimage1container}>
								<MediaCarousel
									previewImageIndex={1}
									key={"top-carousel"}
									className={styles.swipermain}
									galleryItems={topCarouselMedia}
									onSwiper={setTopSwiper}
									onSlideChange={() => {}}
									CustomVideoComponent={DogBioVideoPreview}
									CustomImageComponent={SmallImage}
									pagination={false}
									allowTouchMove={false}
									grabCursor={false}
									longSwipes={false}
									resistance={false}
									shortSwipes={false}
									isDog={isDog}
								/>
							</div>
							<div className={styles.previewimage2container}>
								<MediaCarousel
									previewImageIndex={2}
									key={"bottom-carousel"}
									className={styles.swipermain}
									galleryItems={bottomCarouselMedia}
									onSwiper={setBottomSwiper}
									CustomVideoComponent={DogBioVideoPreview}
									CustomImageComponent={SmallImage}
									pagination={false}
									allowTouchMove={false}
									grabCursor={false}
									longSwipes={false}
									resistance={false}
									shortSwipes={false}
									isDog={isDog}
								/>
							</div>
						</>
					)}
					{media.length > 1 && (
						<div className={styles.paginationcontainer}>
							<Pagination
								onFirstClick={() => {
									swiperRef.current.swiper.slideTo(0);
									topSwiper?.slideTo(0);
									bottomSwiper?.slideTo(0);
								}}
								firstDisabled={currentSlide === 0}
								onPreviousClick={() => {
									swiperRef.current.swiper.slidePrev();
									topSwiper?.slidePrev();
									bottomSwiper?.slidePrev();
								}}
								previousDisabled={currentSlide === 0}
								onNextClick={() => {
									swiperRef.current.swiper.slideNext();
									topSwiper?.slideNext();
									bottomSwiper?.slideNext();
								}}
								nextDisabled={currentSlide === media.length - 1}
								onLastClick={() => {
									swiperRef.current.swiper.slideTo(media.length - 1);
									topSwiper?.slideTo(media.length - 1);
									bottomSwiper?.slideTo(media.length - 1);
								}}
								lastDisabled={currentSlide === media.length - 1}
								position={currentSlide}
								total={media.length}
							/>
						</div>
					)}
				</div>
			) : (
				<div className={styles.singleimagecontainer}>
					<div className={styles.onlyimagecontainer}>
						<img
							className={styles.defaultimage}
							src={defaultDogImage}
							width={400}
							height={300}
							alt=""
							aria-hidden="true"
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default IntroTripleCarousel;
